import './DailyRembleCard.scss'

import { useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { Card } from 'react-bootstrap'
import parse from 'html-react-parser'

import { navigateTo } from 'components/common/navigation'
import {
  ROUTE_DAILY_REMBLE,
  ROUTE_TODAY
} from 'components/common/helpers/Paths'
import { buildRequestURL } from 'components/common/api'

/**
 * Helper function to inject a preload link tag into the head
 * See https://web.dev/optimize-lcp/?utm_source=lighthouse&utm_medium=devtools#preload-important-resources for more info
 * <!-- Preload the LCP image with a high fetchpriority so it starts loading with the stylesheet. -->
 * <link rel="preload" fetchpriority="high" as="image" href="/path/to/hero-image.webp" type="image/webp">
 * @param {*} url The URL of the resource to preload
 * @param {*} type The type of resource to preload
 */
const _injectPreload = (url, type) => {
  // If we've already injected this link, don't do it again
  if (document.querySelector(`link[href="${url}"]`)) return

  const link = document.createElement('link')
  link.rel = 'preload'
  link.href = url
  link.as = type
  document.head.appendChild(link)
}

/**
 * Daily Remble Card component. Used to display a Daily Remble inline preview
 */
export default function DailyRembleCard({
  dailyRemble,
  cardIndex,
  returnPath = ROUTE_TODAY
}) {
  // Utility
  const history = useHistory()
  const previewRef = useRef()

  // Get author name
  const author = dailyRemble?.acf_extended?.author[0]?.post_title

  // Get the Mux Stream ID
  const muxStreamId = dailyRemble?.acf_extended?.mux_stream_id

  // Get the Duration
  const duration = dailyRemble?.acf_extended?.video_duration

  // Get the preview images
  const thumbnailSrc = `https://image.mux.com/${muxStreamId}/thumbnail.jpg?width=200`
  const animatedSrc = `https://image.mux.com/${muxStreamId}/animated.gif?width=200&fps=24`

  /**
   * Event handler for when the user clicks to see the daily remble video vard
   */
  const handleCardClick = () => {
    // Navigate to the daily remble video page
    const uri = `${ROUTE_DAILY_REMBLE}/${dailyRemble.id || dailyRemble.ID}`
    const url = buildRequestURL(uri, { returnPath })
    navigateTo(url, history)
  }

  // If this is the very first daily remble, inject the preload link tags, since we will show the animated preview
  if (cardIndex === 0) _injectPreload(animatedSrc, 'image')

  return (
    dailyRemble && (
      <Card
        className='daily-remble-card'
        onClick={handleCardClick}
        onMouseEnter={() => {
          // Show animated preview on mouse enter
          if (previewRef.current) previewRef.current.src = animatedSrc
        }}
        onMouseLeave={() => {
          // Revert back to static preview on mouse leave
          if (previewRef.current)
            previewRef.current.src =
              cardIndex === 0 ? animatedSrc : thumbnailSrc
        }}
      >
        <div className='daily-remble-card-overlay'>
          <div className='daily-remble-card-overlay-meta'>
            <div className='daily-remble-card-duration'>{duration}</div>
            <div className='daily-remble-card-text'>
              <h5 className='daily-remble-title'>
                {(dailyRemble?.title && parse(dailyRemble.title.rendered)) ||
                  dailyRemble?.post_title}
              </h5>
              <h6 className='daily-remble-author'>{author && parse(author)}</h6>
            </div>
          </div>
        </div>
        <div className='daily-remble-card-preview'>
          {/* Only show the preview if we have a mux playback ID */}
          {muxStreamId && (
            <img
              fetchpriority={cardIndex < 4 ? 'high' : 'auto'}
              loading='lazy'
              ref={previewRef}
              data-index={cardIndex}
              className='daily-remble-card-preview-image'
              alt={
                (dailyRemble?.title && parse(dailyRemble.title.rendered)) ||
                dailyRemble?.post_title
              }
              src={cardIndex === 0 ? animatedSrc : thumbnailSrc}
            />
          )}
        </div>
      </Card>
    )
  )
}
